/**
 * Visual themes, quality tiers and persistent user settings.
 * Colour values obey the four Visual Laws (卷五):
 *  - warm light, nameable materials, restraint (jewel tones belong to marbles
 *    only), and deliberate imperfection.
 */
import type { AIStrategy, Difficulty } from '../game/ai.js';
import type { PlayerCount } from '../constants/camps.js';

export interface ThemePreset {
  id: string;
  nameZh: string;
  nameEn: string;
  /** Felt surface (S ≤ 0.65, V 0.20–0.32). */
  felt: string;
  feltDeep: string;
  /** Wooden frame (oak / walnut / ebony). */
  wood: string;
  woodDeep: string;
  /** Brass hardware — star inlays & hinges. */
  brass: string;
  /** Room backdrop (near-black warm). */
  room: string;
  /** Felt star tint drawn under each camp tip. */
  campTint: boolean;
}

export const THEMES: Record<string, ThemePreset> = {
  heritage: {
    id: 'heritage',
    nameZh: '翡翠阁',
    nameEn: 'Heritage Emerald',
    felt: '#1B4D3E',
    feltDeep: '#0F2F26',
    wood: '#C19A6B',
    woodDeep: '#8A6A45',
    brass: '#D4AF37',
    room: '#161009',
    campTint: true,
  },
  burgundy: {
    id: 'burgundy',
    nameZh: '勃艮第',
    nameEn: 'Burgundy Velvet',
    felt: '#6E3138',
    feltDeep: '#3E1A20',
    wood: '#5C4033',
    woodDeep: '#3B2A21',
    brass: '#C9A227',
    room: '#150E0C',
    campTint: true,
  },
  inkstone: {
    id: 'inkstone',
    nameZh: '砚墨',
    nameEn: 'Inkstone Slate',
    felt: '#2C313A',
    feltDeep: '#181B21',
    wood: '#4A3B2E',
    woodDeep: '#2E241C',
    brass: '#B9A180',
    room: '#0E0F12',
    campTint: false,
  },
  midnight: {
    id: 'midnight',
    nameZh: '午夜蓝丝绒',
    nameEn: 'Midnight Sapphire',
    felt: '#23364E',
    feltDeep: '#131F2E',
    wood: '#3E2F23',
    woodDeep: '#271D15',
    brass: '#B08D57',
    room: '#0B0F16',
    campTint: true,
  },
};

export const THEME_ORDER = ['heritage', 'burgundy', 'inkstone', 'midnight'] as const;

/** Guarded default theme — module-load assertion keeps `!` out of call sites. */
const HERITAGE = THEMES.heritage;
if (!HERITAGE) throw new Error('Theme registry is missing the "heritage" default');
export const DEFAULT_THEME: ThemePreset = HERITAGE;

export interface QualityTier {
  id: 'low' | 'medium' | 'high';
  pixelRatioCap: number;
  shadows: boolean;
  softShadows: boolean;
  shadowMapSize: number;
  dustParticles: number;
  envMap: boolean;
}

export const QUALITY_TIERS: Record<QualityTier['id'], QualityTier> = {
  low: {
    id: 'low',
    pixelRatioCap: 1,
    shadows: false,
    softShadows: false,
    shadowMapSize: 1024,
    dustParticles: 0,
    envMap: false,
  },
  medium: {
    id: 'medium',
    pixelRatioCap: 1.5,
    shadows: true,
    softShadows: true,
    shadowMapSize: 2048,
    dustParticles: 140,
    envMap: true,
  },
  high: {
    id: 'high',
    pixelRatioCap: 2,
    shadows: true,
    softShadows: true,
    shadowMapSize: 2048,
    dustParticles: 320,
    envMap: true,
  },
};

export type QualityId = QualityTier['id'];
/**
 * 对局模式：
 *  - `hotseat` 同屏多人
 *  - `ai`      人机对弈
 *  - `online`  联机（v0.5 预留；随机匹配功能渐进落地）
 *  - `team`    本地组队对战（v4.0 · 2v2/3v3，人+AI 自由搭配，SCREENS §15）
 */
export type Mode = 'hotseat' | 'ai' | 'online' | 'team';
export type Language = 'zh' | 'en';

/**
 * 组队对战座位配置（v4.0 · SCREENS §15.2）。
 * 座位按回合顺序排列；队伍由座次派生（RULES §11：偶数回合位 = A 队，
 * 奇数 = B 队），面板只决定"谁坐哪"，从不手挑队友。
 */
export interface TeamSeatConfig {
  kind: 'human' | 'ai';
  /** AI 座位专用（human 座位忽略）；策略 × 棋力正交（AI_STRATEGIES §5）。 */
  strategy: AIStrategy;
  difficulty: Difficulty;
}

export interface TeamMatchConfig {
  format: '2v2' | '3v3';
  /** 回合顺序座位，长度 4（2v2）/ 6（3v3）。 */
  seats: TeamSeatConfig[];
}

/** 默认组队：我 + 协作型 AI 搭档（A 队） vs 进攻 + 均衡（B 队），棋力普通。 */
export const DEFAULT_TEAM_MATCH: TeamMatchConfig = {
  format: '2v2',
  seats: [
    { kind: 'human', strategy: 'balanced', difficulty: 'medium' },
    { kind: 'ai', strategy: 'aggressive', difficulty: 'medium' },
    { kind: 'ai', strategy: 'cooperative', difficulty: 'medium' },
    { kind: 'ai', strategy: 'balanced', difficulty: 'medium' },
  ],
};

export interface GameSettings {
  themeId: string;
  quality: QualityId;
  mode: Mode;
  playerCount: PlayerCount;
  aiDifficulty: Difficulty;
  sfxVolume: number;
  ambientVolume: number;
  muted: boolean;
  showHints: boolean;
  turnTimerSec: number; // 0 = off
  language: Language;
  marbleIridescence: number; // 0–1, 玻璃虹彩强度
  animationSpeed: number; // 0.5–2×
  /** 组队对战配置快照（v4.0 · mode:'team' 时生效）。 */
  teamMatch: TeamMatchConfig;
}

export const DEFAULT_SETTINGS: GameSettings = {
  themeId: 'heritage',
  quality: 'medium',
  mode: 'ai',
  playerCount: 2,
  aiDifficulty: 'medium',
  sfxVolume: 0.8,
  ambientVolume: 0.35,
  muted: false,
  showHints: true,
  turnTimerSec: 0,
  language: 'zh',
  marbleIridescence: 0.85,
  animationSpeed: 1,
  teamMatch: DEFAULT_TEAM_MATCH,
};

const STORAGE_KEY = 'mc.settings.v1';

export function loadSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: GameSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable — settings stay session-only */
  }
}

/** Export a shareable template JSON (模板化). */
export function exportTemplate(s: GameSettings): string {
  return JSON.stringify(s, null, 2);
}

export function importTemplate(json: string): GameSettings | null {
  try {
    const parsed = JSON.parse(json) as Partial<GameSettings>;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return null;
  }
}
